/** The student's OWN released record, read from the analytics service.
 *
 * `/analytics/v1/me/*` is keyed on the caller's subject, so there is no shape of request that
 * could return a classmate. It is also where `report_release` lives: a course that releases
 * per session is left out until its teacher releases one, and a `guest_only` course never
 * mints a subject at all, so it can never appear. That makes this endpoint the release gate
 * as well as the data — the device is never the one deciding a student may look.
 */
import { useQuery } from "@tanstack/react-query";

import { serviceUrl } from "../api/base";
import { ApiError, get } from "../api/client";
import { accessToken } from "../auth/session";
import { lastJoin } from "../live/studentSession";

export type Cell = { lo_id: string; code: string; text: string; score: number; band: string; n: number; last_assessed_at: string | null };
export type Course = { course_id: string; title: string; respondent_id: string; release_mode: string; released_sessions: number; los: Cell[] };
export type Mine = { subject: string; courses: Course[] };

/** The mastery bands on a student screen: a fill glyph and a colour, and every use of them
 * carries the band's name in words beside it, so the band never rests on colour alone. */
export const BAND_GLYPH: Record<string, string> = { secure: "■", developing: "▦", not_yet: "□", unassessed: "·" };
export const BAND_TONE: Record<string, string> = {
  secure: "var(--secure)",
  developing: "var(--developing)",
  not_yet: "var(--not-yet)",
  unassessed: "var(--ink-2)",
};

/** The caller's principal: a signed-in account where there is one, otherwise the guest token
 * this device got when it joined. The device key is not itself a cloud credential — it is the
 * merge key the teacher systems record — so these reads still need one of those two. */
export async function meGet<T>(path: string, guestToken?: string | null): Promise<T> {
  if (accessToken()) return get<T>(path);
  const guest = guestToken ?? lastJoin()?.guest_token;
  if (!guest) throw new ApiError(401, "no principal on this device", "no_principal");
  const res = await fetch(serviceUrl(path), { headers: { Authorization: `Bearer ${guest}` } });
  if (!res.ok) throw new ApiError(res.status, `GET ${path}`, res.status === 401 ? "no_principal" : undefined);
  return (await res.json()) as T;
}

/** `guestToken` names the sitting to read as, so a student sitting in one lesson is not read
 * with the token of another they joined earlier in the day. */
export function useMine(guestToken?: string | null) {
  return useQuery({
    queryKey: ["my", "mastery", guestToken ?? "account"],
    queryFn: () => meGet<Mine>("/analytics/v1/me/mastery", guestToken),
    retry: false,
  });
}
